import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export default function EmailAction() {
  const [params] = useSearchParams();
  const t = params.get("t");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [rationale, setRationale] = useState("");
  const [conditions, setConditions] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/email-actions/preview?t=${encodeURIComponent(t || "")}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e)));
  }, [t]);
  const approve = info?.decision === "approve";
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/email-actions/confirm", { t, rationale, conditions });
      setDone(r.data);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg rounded-2xl border bg-white p-6 shadow-sm" data-testid="email-action-page">
        <div className="mb-5 flex items-center gap-2"><div className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 font-display font-bold text-white">A</div><span className="font-display text-lg font-semibold">Aspire</span></div>
        {err && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="email-action-error">{err}</div>}
        {!info && !err && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
        {done && (
          <div className="text-center" data-testid="email-action-done">
            {done.decision === "approve" ? <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" /> : <XCircle className="mx-auto h-10 w-10 text-red-600" />}
            <h2 className="mt-3 text-xl font-semibold">Decision recorded</h2>
            <p className="mt-1 text-sm text-slate-500">{done.status === "pending" ? "Routed to the next step." : `Item is now ${done.status}.`} The requester has been notified.</p>
          </div>
        )}
        {info && !done && (
          <div className="space-y-4">
            <div>
              <div className="overline">{info.item_type} · {info.project}</div>
              <h1 className="text-xl font-semibold" data-testid="email-action-title">{info.title}</h1>
              <div className="text-xs text-slate-500">Raised by {info.requester} · deciding as {info.approver_name}{info.step && ` · ${info.step}`}</div>
            </div>
            <ul className="space-y-1 text-xs text-slate-600">{info.history.map((h) => <li key={`${h.label}-${h.status}-${h.by || ""}`}><b>{h.label}</b> — {h.status}{h.by && ` by ${h.by}`}{h.rationale && `: ${h.rationale}`}</li>)}</ul>
            {info.state !== "ok" ? (
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="email-action-state">This link can no longer be used ({info.state}). <Link className="underline" to={`/approvals?open=${info.approval_id}`}>Open in Aspire</Link></div>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Rationale (mandatory)</Label><Textarea data-testid="email-action-rationale" value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="One line on why — it is recorded in the audit trail." /></div>
                <div className="space-y-1.5"><Label>Conditions (optional)</Label><Textarea rows={2} data-testid="email-action-conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} /></div>
                <Button data-testid="email-action-confirm" disabled={busy || rationale.trim().length < 3} onClick={confirm} className={`w-full ${approve ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm {approve ? (info.verb === "decide" ? "approval" : info.verb) : "rejection"}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
